import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Flag, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Stars } from "@/components/seller-rating-inline";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

const PAGE_SIZE = 10;
const REPORT_MIN = 10;
const REPORT_MAX = 500;

type PublicReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  buyer_id: string;
};

type ReviewWithName = PublicReview & { buyer_display_name: string | null };

type Cursor = { created_at: string; id: string } | null;

function ukDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

async function fetchReviewsPage(
  sellerId: string,
  cursor: Cursor,
): Promise<{ rows: ReviewWithName[]; nextCursor: Cursor }> {
  let q = supabase
    .from("public_reviews" as never)
    .select("id,rating,comment,created_at,buyer_id")
    .eq("seller_id", sellerId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(PAGE_SIZE + 1);
  if (cursor) {
    // keyset: (created_at, id) strictly less than the cursor
    q = q.or(
      `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
    );
  }
  const { data, error } = await q;
  if (error) throw error;
  const rowsRaw = (data ?? []) as PublicReview[];
  const hasMore = rowsRaw.length > PAGE_SIZE;
  const rows = hasMore ? rowsRaw.slice(0, PAGE_SIZE) : rowsRaw;

  const buyerIds = Array.from(new Set(rows.map((r) => r.buyer_id)));
  let nameMap = new Map<string, string | null>();
  if (buyerIds.length > 0) {
    const { data: profs, error: perr } = await supabase
      .from("public_profiles" as never)
      .select("id,display_name")
      .in("id", buyerIds);
    if (perr) throw perr;
    nameMap = new Map(
      ((profs ?? []) as { id: string; display_name: string | null }[]).map((p) => [
        p.id,
        p.display_name,
      ]),
    );
  }
  const withNames: ReviewWithName[] = rows.map((r) => ({
    ...r,
    buyer_display_name: nameMap.get(r.buyer_id) ?? null,
  }));
  const last = withNames[withNames.length - 1];
  const nextCursor: Cursor = hasMore && last ? { created_at: last.created_at, id: last.id } : null;
  return { rows: withNames, nextCursor };
}

export function SellerReviewsSection({ sellerId }: { sellerId: string }) {
  const { t } = useTranslation("marketing");
  const [items, setItems] = useState<ReviewWithName[]>([]);
  const [cursor, setCursor] = useState<Cursor>(null);
  const [hasMore, setHasMore] = useState(true);
  const [reportedIds, setReportedIds] = useState<Set<string>>(new Set());

  const initial = useQuery({
    queryKey: ["seller-reviews-initial", sellerId],
    queryFn: async () => {
      const res = await fetchReviewsPage(sellerId, null);
      setItems(res.rows);
      setCursor(res.nextCursor);
      setHasMore(res.nextCursor !== null);
      return res;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const loadMore = useMutation({
    mutationFn: async () => {
      if (!cursor) return { rows: [] as ReviewWithName[], nextCursor: null as Cursor };
      return fetchReviewsPage(sellerId, cursor);
    },
    onSuccess: (res) => {
      if (res.rows.length > 0) {
        // Deduplicate against existing items just in case.
        setItems((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...res.rows.filter((r) => !seen.has(r.id))];
        });
      }
      setCursor(res.nextCursor);
      setHasMore(res.nextCursor !== null);
    },
    onError: (e: Error) => toast.error(e.message || t("seller.loadMoreFailed", "Could not load more reviews")),
  });

  if (initial.isLoading) {
    return <div className="text-sm text-muted-foreground">{t("seller.loadingReviews", "Loading reviews…")}</div>;
  }

  if (initial.isError && items.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-6 text-sm">
        <p className="text-muted-foreground">{t("seller.reviewsCouldNotLoad", "Reviews couldn't be loaded.")}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => initial.refetch()}
        >
          {t("seller.tryAgain", "Try again")}
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
        {t("seller.noReviewsBody", "This seller has no reviews yet.")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.id} className="rounded-xl border border-border/60 bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Stars rating={r.rating} />
                <span className="text-sm font-medium">
                  {r.buyer_display_name?.trim() || t("seller.anonymousBuyer", "BidNova Auctions buyer")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <time className="text-xs text-muted-foreground" dateTime={r.created_at}>
                  {ukDate(r.created_at)}
                </time>
                <ReportReviewAction
                  sellerId={sellerId}
                  reviewId={r.id}
                  reported={reportedIds.has(r.id)}
                  onReported={() =>
                    setReportedIds((prev) => {
                      const next = new Set(prev);
                      next.add(r.id);
                      return next;
                    })
                  }
                />
              </div>
            </div>
            <p
              className={
                r.comment?.trim()
                  ? "mt-2 text-sm whitespace-pre-line"
                  : "mt-2 text-sm italic text-muted-foreground"
              }
            >
              {r.comment?.trim() ? r.comment : t("seller.noWrittenComment", "No written comment")}
            </p>
          </li>
        ))}
      </ul>
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => {
              if (!loadMore.isPending) loadMore.mutate();
            }}
            disabled={loadMore.isPending}
            aria-label={t("seller.loadMoreReviews", "Load more reviews")}
          >
            {loadMore.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                {t("seller.loadingMore", "Loading…")}
              </>
            ) : (
              t("seller.loadMoreReviews", "Load more reviews")
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReportReviewAction({
  sellerId,
  reviewId,
  reported,
  onReported,
}: {
  sellerId: string;
  reviewId: string;
  reported: boolean;
  onReported: () => void;
}) {
  const { t } = useTranslation("marketing");
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const trimmed = reason.trim();
      if (trimmed.length < REPORT_MIN)
        throw new Error(t("seller.reportMinChars", "Please provide at least {min} characters", { min: REPORT_MIN }));
      if (trimmed.length > REPORT_MAX) throw new Error(t("seller.reportTooLong", "Reason is too long"));
      const { error } = await supabase.rpc(
        "report_review" as never,
        { _review_id: reviewId, _reason: trimmed } as never,
      );
      if (error) throw new Error(error.message || t("seller.reportSubmitFailed", "Could not submit report"));
    },
    onSuccess: () => {
      toast.success(t("seller.reportSubmitted", "Report submitted. Thanks for flagging."));
      onReported();
      setOpen(false);
      setReason("");
      qc.invalidateQueries({ queryKey: ["seller-reviews-initial"] });
    },
    // keep dialog open on error; toast provides feedback
    onError: (e: Error) => toast.error(e.message || t("seller.reportSubmitFailed", "Could not submit report")),
  });

  if (reported) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-300"
        aria-label={t("seller.reported", "Reported")}
      >
        <Check className="h-3.5 w-3.5" aria-hidden /> {t("seller.reported", "Reported")}
      </span>
    );
  }

  if (!user) {
    return (
      <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
        <Link
          to="/auth"
          search={{ mode: "signin", redirect: `/seller/${sellerId}` } as never}
          aria-label={t("seller.signInToReport", "Sign in to report this review")}
        >
          <Flag className="h-3.5 w-3.5 mr-1" aria-hidden /> {t("seller.report", "Report")}
        </Link>
      </Button>
    );
  }

  const trimmedLen = reason.trim().length;
  const tooShort = trimmedLen < REPORT_MIN;
  const tooLong = reason.length > REPORT_MAX;
  const disabled = mutation.isPending || tooShort || tooLong;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (mutation.isPending) return;
        setOpen(v);
        if (!v) setReason("");
      }}
    >
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs"
        onClick={() => setOpen(true)}
        aria-label={t("seller.reportThisReview", "Report this review")}
      >
        <Flag className="h-3.5 w-3.5 mr-1" aria-hidden /> {t("seller.report", "Report")}
      </Button>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{t("seller.reportDialogTitle", "Report this review")}</DialogTitle>
          <DialogDescription>
            {t("seller.reportDialogDescription", "Tell our moderators why this review breaches our policies. Your report is private and reviewed by the BidNova Auctions team.")}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor={`report-reason-${reviewId}`}>{t("seller.reportReasonLabel", "Reason")}</Label>
          <Textarea
            id={`report-reason-${reviewId}`}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, REPORT_MAX))}
            placeholder={t("seller.reportPlaceholder", "e.g. contains personal information, spam, abusive language…")}
            rows={5}
            maxLength={REPORT_MAX}
            className="mt-1"
            aria-describedby={`report-count-${reviewId}`}
          />
          <p
            id={`report-count-${reviewId}`}
            className={cn(
              "mt-1 flex items-center justify-between text-xs",
              tooShort ? "text-muted-foreground" : "text-emerald-300",
            )}
            aria-live="polite"
          >
            <span>
              {tooShort
                ? t("seller.reportTooShort", "At least {min} characters required", { min: REPORT_MIN })
                : t("seller.reportLooksGood", "Looks good")}
            </span>
            <span className={cn(tooLong && "text-red-400")}>
              {reason.length} / {REPORT_MAX}
            </span>
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={mutation.isPending}
          >
            {t("seller.cancel", "Cancel")}
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={disabled}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />}
            {t("seller.submitReport", "Submit report")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
